import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { cn, useToast } from '@mustauto/ui/web';
import { formatApiError } from '@mustauto/utils';
import type { InShopColumn, InShopOrder } from '@mustauto/types/in-shop';
import { api } from '@/lib/api';
import type { InShopEntry, InShopLocation } from '@/hooks/useCarsInShop';
import { InShopColumnChecklist } from './InShopColumnsPicker';
import { InShopOrderToggle } from './InShopOrderToggle';

type Format = 'pdf' | 'csv';

const FORMATS: { key: Format; title: string; hint: string }[] = [
  { key: 'pdf', title: 'PDF', hint: 'para imprimir e deixar no balcão' },
  { key: 'csv', title: 'Planilha (CSV)', hint: 'abre no Excel, com filtro por coluna' },
];

/** Acima disto o PDF (A4 deitado) aperta e corta texto longo. Conta lugar e placa. */
const PDF_COMFORTABLE_COLUMNS = 10;

/**
 * "Exportar": formato, colunas, ordem e quais carros. Colunas e ordem são as
 * da tela — mudar aqui muda lá, é uma escolha só. O recorte por lugar é só da
 * exportação e não fica guardado: toda exportação começa com todos.
 */
export function InShopExportDialog({
  open,
  onClose,
  vehicles,
  groups,
  columns,
  onColumnsChange,
  order,
  onOrderChange,
}: {
  open: boolean;
  onClose: () => void;
  vehicles: InShopEntry[];
  /** Os lugares com carro agora, na ordem da tela. */
  groups: { key: InShopLocation; title: string; count: number }[];
  columns: readonly InShopColumn[];
  onColumnsChange: (next: InShopColumn[]) => void;
  order: InShopOrder;
  onOrderChange: (next: InShopOrder) => void;
}) {
  const { toast } = useToast();
  const [format, setFormat] = useState<Format>('pdf');
  const [excluded, setExcluded] = useState<Set<InShopLocation>>(new Set());
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const included = groups.filter((g) => !excluded.has(g.key));
  const carCount = vehicles.filter((v) => !excluded.has(v.location)).length;
  const totalColumns = columns.length + 2;

  const toggleGroup = (key: InShopLocation) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const close = () => {
    if (loading) return;
    setExcluded(new Set());
    onClose();
  };

  const download = async () => {
    setLoading(true);
    try {
      const res = await api.get('/service-orders/in-shop/export', {
        params: {
          format,
          order,
          columns: columns.join(','),
          // Só quando a pessoa tirou algum lugar. Mandar a lista dos grupos da
          // tela tiraria os lugares sem carro — e com eles o "pronto", cujo
          // aviso das concluídas antigas o PDF deixaria de imprimir.
          ...(excluded.size > 0 ? { locations: included.map((g) => g.key).join(',') } : {}),
        },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: format === 'csv' ? 'text/csv' : 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `carros-na-oficina.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      setExcluded(new Set());
      onClose();
    } catch (err) {
      toast({
        type: 'error',
        title: 'Falha ao exportar',
        description: formatApiError(err, 'Não foi possível gerar o arquivo. Tente novamente em instantes.'),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center" onClick={close}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="in-shop-export-title"
        className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-t-xl bg-background shadow-xl sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pb-3 pt-5">
          <h3 id="in-shop-export-title" className="text-lg font-semibold">
            Exportar carros na oficina
          </h3>
          <p className="text-sm text-muted-foreground">As colunas e a ordem vêm da tela; mudar aqui muda lá também.</p>
        </div>

        <section className="border-t border-border px-6 py-4">
          <p className="mb-2 text-sm font-semibold">Formato</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {FORMATS.map((f) => (
              <button
                key={f.key}
                type="button"
                aria-pressed={format === f.key}
                onClick={() => setFormat(f.key)}
                className={cn(
                  'rounded-lg border-2 px-3 py-2.5 text-left',
                  format === f.key ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted',
                )}
              >
                <span className="block text-sm font-semibold">{f.title}</span>
                <span className="text-xs text-muted-foreground">{f.hint}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="border-t border-border px-6 py-4">
          <p className="mb-1 text-sm font-semibold">Colunas</p>
          <InShopColumnChecklist
            columns={columns}
            onChange={onColumnsChange}
            idPrefix="in-shop-export-col"
            className="grid grid-cols-1 gap-x-6 sm:grid-cols-2"
          />
          {format === 'pdf' && totalColumns > PDF_COMFORTABLE_COLUMNS && (
            <p className="mt-2 text-xs text-muted-foreground">
              Com {totalColumns} colunas o PDF fica apertado e corta textos longos. A planilha cabe tudo.
            </p>
          )}
        </section>

        {groups.length > 0 && (
          <section className="border-t border-border px-6 py-4">
            <p className="mb-1 text-sm font-semibold">Quais carros</p>
            <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
              {groups.map((g) => (
                <label
                  key={g.key}
                  htmlFor={`in-shop-export-loc-${g.key}`}
                  className="flex cursor-pointer items-center gap-2.5 py-1.5 text-sm"
                >
                  <input
                    id={`in-shop-export-loc-${g.key}`}
                    type="checkbox"
                    checked={!excluded.has(g.key)}
                    onChange={() => toggleGroup(g.key)}
                    className="h-4 w-4 accent-primary"
                  />
                  {g.title}
                  <span className="ml-auto text-xs tabular-nums text-muted-foreground">{g.count}</span>
                </label>
              ))}
            </div>
          </section>
        )}

        <section className="flex flex-wrap items-center gap-3 border-t border-border px-6 py-4">
          <p className="text-sm font-semibold">Ordem</p>
          <InShopOrderToggle order={order} onChange={onOrderChange} />
        </section>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-6 py-4">
          <span className="text-xs text-muted-foreground">
            {carCount} {carCount === 1 ? 'carro' : 'carros'} · {totalColumns} colunas
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-md border px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
              onClick={close}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
              disabled={loading || (groups.length > 0 && included.length === 0)}
              onClick={() => void download()}
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {format === 'pdf' ? 'Baixar PDF' : 'Baixar planilha'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
